<template>
  <div class="tender-description">
    <div class="title">{{ $t('tender-details.informations.description') }}</div>

    <template v-if="tender.description">
      <div
        :id="contentId"
        ref="content"
        class="tender-description__content"
        :class="{ 'is-folded': isFolded }"
        :style="isFolded ? { maxHeight: `${foldedHeight}px` } : null"
      >
        <TextHighlight :content="tender.description"></TextHighlight>
      </div>
      <button
        v-if="foldable"
        type="button"
        class="tender-description__toggle"
        :aria-expanded="String(!folded)"
        :aria-controls="contentId"
        @click="folded = !folded"
      >
        {{ folded ? $t('tender-details.informations.read-more') : $t('tender-details.informations.read-less') }}
      </button>
    </template>
    <div v-else class="tender-description__content">{{ $t('tender-details.informations.description-empty') }}</div>
  </div>
</template>

<script>
import TextHighlight from '@/components/text-highlight/TextHighlight';
import { viewport } from '@/util/viewport';

// A folded description shows this many lines.
const FOLDED_LINES = 12;

export default {
  name: 'TenderDescription',
  components: {
    TextHighlight,
  },
  props: {
    tender: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      contentId: `tender-description-${this._uid}`,
      folded: true,
      // The height that shows FOLDED_LINES, and the description's full height.
      foldedHeight: 0,
      fullHeight: 0,
    };
  },
  computed: {
    screenSize() {
      return viewport.size;
    },
    // On a phone a long description folds: in one column, it would push the documents, the
    // bidders and the opportunities a few screens down.
    foldable() {
      return this.screenSize === 'phone' && this.foldedHeight > 0 && this.fullHeight > this.foldedHeight + 1;
    },
    isFolded() {
      return this.foldable && this.folded;
    },
  },
  watch: {
    // Another tender (next, previous): folded again, and measured again.
    'tender.id'() {
      this.folded = true;
      this.$nextTick(this.measure);
    },
    screenSize() {
      this.$nextTick(this.measure);
    },
  },
  methods: {
    measure() {
      const content = this.$refs.content;
      if (!content) return;
      const style = window.getComputedStyle(content);
      this.foldedHeight = parseFloat(style.lineHeight) * FOLDED_LINES + parseFloat(style.paddingTop) || 0;
      this.fullHeight = content.scrollHeight;
    },
  },
  mounted() {
    this.measure();
  },
};
</script>

<style lang="scss">
.tender-description {
  padding: 2rem;
  margin-right: 1rem;
  margin-bottom: 1rem;
  background: $color-white;
  border-radius: $border-radius-root;

  &__content {
    font-size: 14px;
    color: $color-normal-pale;
    font-family: $font-families;
    line-height: 1.4rem;
    padding-top: 16px;
    white-space: pre-wrap;
    // A long link or reference would otherwise run past the card.
    overflow-wrap: anywhere;

    // Fading out at the bottom.
    &.is-folded {
      overflow: hidden;
      mask-image: linear-gradient(to bottom, #000 70%, transparent);
    }
  }

  &__toggle {
    margin-top: 8px;
    padding: 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: $color-accent;
  }

  @media screen and (max-width: 1080px) {
    margin-right: 0px;
    margin-bottom: 1rem;
  }
}
</style>
